import { ReactNode, useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { User, ChevronDown, Heart, History, Settings, LogOut, Star, MapPin, Clock, X, TrendingDown, Search, Crown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import QuickAuthModal from './QuickAuthModal'
import FavoritesModal from './FavoritesModal'
import CustomerFeedback from './CustomerFeedback'
import { useGlobalSearch } from '../hooks/useGlobalSearch'
import SearchAutocomplete from './SearchAutocomplete'

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout, isPremium, premiumAccess } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showAccountDropdown, setShowAccountDropdown] = useState(false)
  const [showFavoritesModal, setShowFavoritesModal] = useState(false)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const mobileSearchInputRef = useRef<HTMLInputElement>(null)

  // Global search hook
  const {
    query,
    setQuery,
    results,
    isLoading,
    isOpen,
    openSearch,
    closeSearch,
    debouncedSearch,
  } = useGlobalSearch()

  // Check if we're on a shoe detail page
  const isShoeDetailPage = location.pathname.startsWith('/shoe/')

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAccountDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus mobile search input when modal opens
  useEffect(() => {
    if (showMobileSearch && mobileSearchInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        mobileSearchInputRef.current?.focus()
      }, 100)
    }
  }, [showMobileSearch])

  // Extract username from email (part before @)
  const getEmailUsername = (email: string, maxLength: number = 15) => {
    if (!email) return email

    const [localPart] = email.split('@')
    if (!localPart) return email

    // If local part is too long, truncate it
    if (localPart.length > maxLength) {
      return localPart.substring(0, maxLength - 3) + '...'
    }

    return localPart
  }

  // Handle authentication modal
  const handleAuthSuccess = () => {
    setShowAuthModal(false)
  }

  const handleAuthClose = () => {
    setShowAuthModal(false)
  }

  const renderAuthSection = () => {
    if (!isAuthenticated) {
      // Not logged in - show Sign In button
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAuthModal(true)}
            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors"
            title="Sign in to track favorites and get personalized recommendations"
          >
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Sign In</span>
          </button>
        </div>
      )
    }

    // Logged in user - show dropdown menu
    return (
      <div className="flex items-center gap-2">
        {/* User Dropdown Container */}
        <div className="relative" ref={dropdownRef}>
          {/* User Dropdown Button */}
          <button
            onClick={() => setShowAccountDropdown(!showAccountDropdown)}
            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-xs sm:text-sm font-medium transition-colors max-w-[120px] sm:max-w-xs"
            title={user?.email} // Show full email on hover
          >
            <User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
            <span className="hidden sm:inline text-gray-700 truncate min-w-0">{getEmailUsername(user?.email || '', 15)}</span>
            {isPremium && (
              <Crown className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500 flex-shrink-0" />
            )}
            <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-400 transition-transform flex-shrink-0 ${showAccountDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showAccountDropdown && (
          <>
            {/* Mobile Full Screen Overlay */}
            <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowAccountDropdown(false)} />
            
            {/* Dropdown Menu */}
            <div className="fixed sm:absolute top-16 sm:top-auto left-4 sm:left-auto right-4 sm:right-0 mt-0 sm:mt-2 w-auto sm:w-80 md:w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[80vh] sm:max-h-[70vh] overflow-y-auto">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-gray-100 relative">
              {/* Mobile Close Button */}
              <button 
                className="sm:hidden absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full"
                onClick={() => setShowAccountDropdown(false)}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              
              <div className="space-y-2 pr-12 sm:pr-0">
                <p
                  className="text-sm font-medium text-gray-900 truncate pr-2 max-w-[220px] sm:max-w-48"
                  title={user?.email}
                >
                  {user?.email}
                </p>
                {isPremium && premiumAccess && (
                  <div className="flex items-center space-x-2 text-xs">
                    <div className="flex items-center space-x-1 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 px-2 py-1 rounded-full border border-amber-200">
                      <Crown className="h-3 w-3" />
                      <span className="font-medium">Premium Member</span>
                    </div>
                    <span className="text-gray-500">
                      {premiumAccess.daysRemaining} days left
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              {/* Favorites */}
              <button
                onClick={() => {
                  setShowAccountDropdown(false)
                  setShowFavoritesModal(true)
                }}
                className="flex items-center w-full px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px] sm:min-h-auto"
              >
                <Heart className="h-4 w-4 mr-3 text-gray-400" />
                <span>Favorite Shoes</span>
              </button>

              {/* History */}
              <button
                onClick={() => {
                  setShowAccountDropdown(false)
                  navigate('/history')
                }}
                className="flex items-center w-full px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px] sm:min-h-auto"
              >
                <History className="h-4 w-4 mr-3 text-gray-400" />
                <span>Recommendation History</span>
              </button>

              {/* Account Settings */}
              <button
                onClick={() => {
                  setShowAccountDropdown(false)
                  navigate('/settings')
                }}
                className="flex items-center w-full px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px] sm:min-h-auto"
              >
                <Settings className="h-4 w-4 mr-3 text-gray-400" />
                <span>Account Settings</span>
              </button>

              <div className="border-t border-gray-100 my-1"></div>

              {/* Logout */}
              <button
                onClick={() => {
                  logout()
                  setShowAccountDropdown(false)
                }}
                className="flex items-center w-full px-4 py-3 sm:py-2 text-base sm:text-sm text-red-600 hover:bg-red-50 transition-colors min-h-[44px] sm:min-h-auto"
              >
                <LogOut className="h-4 w-4 mr-3 text-red-500" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 sm:bg-gradient-to-br sm:from-secondary-50 sm:to-secondary-100 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-soft border-b border-secondary-200">
        <div className="w-full mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 sm:h-20 gap-2 sm:gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group flex-shrink-0">
              <div className="rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                {/* Using actual ShoeSaver logo */}
                <img
                  src="/images/shoesaver-logo.png"
                  alt="ShoeSaver Logo" 
                  className="h-8 w-8 sm:h-12 sm:w-12 rounded-xl object-cover"
                />
              </div>
              <span className="text-lg sm:text-2xl font-bold text-orange-600">
                Shoe<span className="text-orange-500">Saver</span>
              </span>
            </Link>

            {/* Slogan */}
            <div className="hidden sm:block text-sm md:text-base text-gray-600 font-medium ml-2 lg:ml-4">
              Fit Better, Save More
            </div>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* All Pages: Search + Auth (on shoe detail page, also show Best Deals button) */}
            <div className="flex items-center flex-shrink-0 gap-2">
              {/* Best Deal Shoes Button - Only on shoe detail pages */}
              {isShoeDetailPage && (
                <Link
                  to="/"
                  className="flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 lg:px-4 lg:py-2 bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-2 border-orange-200 hover:border-orange-300 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-md"
                  title="View Best Deal Shoes"
                >
                  <TrendingDown className="hidden sm:block w-4 h-4 text-orange-600" />
                  <span className="text-xs sm:text-sm font-semibold text-orange-700">Best Deals</span>
                </Link>
              )}

              {/* Search Box (Desktop) - Visible on all pages */}
              <div className="relative hidden md:block">
                <input
                  type="text"
                  placeholder="Find more shoes..."
                  className="w-48 lg:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    debouncedSearch(e.target.value)
                  }}
                  onFocus={openSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && query.length >= 2) {
                      e.preventDefault()
                      navigate(`/?search=${encodeURIComponent(query)}`)
                      closeSearch()
                    }
                  }}
                />
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />

                {/* Search Autocomplete Dropdown */}
                <SearchAutocomplete
                  query={query}
                  results={results}
                  isLoading={isLoading}
                  isOpen={isOpen}
                  onSelect={(shoe) => {
                    navigate(`/shoe/${shoe._id}`)
                    closeSearch()
                    setQuery('')
                  }}
                  onClose={closeSearch}
                />
              </div>

              {/* Mobile Search Icon */}
              <button
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => {
                  setShowMobileSearch(true)
                }}
                title="Search"
              >
                <Search className="w-5 h-5 text-gray-600" />
              </button>

              {/* Authentication Section */}
              {renderAuthSection()}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer - User Testimonials */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
            {/* Testimonial 1 - Price Value */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src="/images/avatars/1.jpeg"
                  alt="Sarah M."
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">Michael D.</h4>
                  <p className="text-xs text-gray-500 truncate">m****l.davis@gmail.com</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                "Found $180 shoes for just $45! Perfect fit for daily runs."
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>California</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>3d ago</span>
                </div>
              </div>
            </div>

            {/* Testimonial 2 - Beginner Runner */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src="/images/avatars/2.jpeg"
                  alt="Mike R."
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">Lisa R.</h4>
                  <p className="text-xs text-gray-500 truncate">l***a.rodriguez@yahoo.com</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                "As a beginner, I was overwhelmed. ShoeSaver found ideal starter shoes!"
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>Texas</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>1w ago</span>
                </div>
              </div>
            </div>

            {/* Testimonial 3 - Marathon Runner */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src="/images/avatars/3.jpeg"
                  alt="Jennifer K."
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">Marcus K.</h4>
                  <p className="text-xs text-gray-500 truncate">m****s.kim@outlook.com</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                "AI nailed my marathon needs. Saved $200 vs local store!"
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>New York</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>5d ago</span>
                </div>
              </div>
            </div>

            {/* Testimonial 4 - Daily Training */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src="/images/avatars/4.jpeg"
                  alt="David L."
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">Rachel L.</h4>
                  <p className="text-xs text-gray-500 truncate">r****l.lopez@gmail.com</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                "Perfect for daily 5-mile runs. Great discount!"
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>Florida</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>2d ago</span>
                </div>
              </div>
            </div>

            {/* Testimonial 5 - Personalized Fit */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src="/images/avatars/6.jpeg"
                  alt="Amanda S."
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">Emily S.</h4>
                  <p className="text-xs text-gray-500 truncate">e***y.smith@icloud.com</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                "Finally found shoes for wide feet! AI understood my needs perfectly."
              </p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>Oregon</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>1w ago</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Copyright */}
          <div className="pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © 2025 ShoeSaver - Fit Better, Save More. Helping runners find their perfect shoes at the best prices.
            </p>
          </div>
        </div>
      </footer>

      {/* Smart Authentication Modal */}
      <QuickAuthModal
        isOpen={showAuthModal}
        onClose={handleAuthClose}
        onSuccess={handleAuthSuccess}
        title="Welcome to ShoeSaver"
      />

      {/* Favorites Modal */}
      <FavoritesModal
        isOpen={showFavoritesModal}
        onClose={() => setShowFavoritesModal(false)}
      />

      {/* Customer Feedback */}
      <CustomerFeedback />

      {/* Mobile Search Modal */}
      {showMobileSearch && (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => {
                setShowMobileSearch(false)
                setQuery('')
                closeSearch()
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close search"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1 relative">
              <input
                ref={mobileSearchInputRef}
                type="text"
                placeholder="Search for shoes..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  debouncedSearch(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.length >= 2) {
                    e.preventDefault()
                    navigate(`/?search=${encodeURIComponent(query)}`)
                    setShowMobileSearch(false)
                    closeSearch()
                  }
                }}
                autoFocus
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="p-4">
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-16 h-16 bg-gray-200 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isLoading && query.length > 0 && query.length < 2 && (
              <div className="p-6 text-center text-gray-500">
                Please enter at least 2 characters to search
              </div>
            )}

            {!isLoading && query.length >= 2 && results.length > 0 && (
              <div className="py-2">
                {results.map((shoe) => (
                  <button
                    key={shoe._id}
                    onClick={() => {
                      navigate(`/shoe/${shoe._id}`)
                      setShowMobileSearch(false)
                      closeSearch()
                      setQuery('')
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-orange-50 transition-colors text-left border-b border-gray-100"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate text-base">
                        {shoe.model}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                        <span>
                          {shoe.price.current
                            ? `$${shoe.price.current}`
                            : 'N/A'}
                        </span>
                        {shoe.price.discount > 0 && (
                          <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                            -{shoe.price.discount}%
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!isLoading &&
              query.length >= 2 &&
              results.length === 0 && (
                <div className="p-6 text-center">
                  <div className="text-gray-500 mb-2">
                    No shoes found for "{query}"
                  </div>
                  <div className="text-sm text-gray-400">
                    Try different keywords or browse our recommendations
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Layout